import { useEffect } from "react";
import { useRecords } from "../../../api/useRecords";
import { Box } from "@mui/material";
import { useStore } from "../../../store/store";
import { useForm } from "react-hook-form";
import { SessionsCalendarHeader } from "./SessionsCalendarHeader";
import { createCalendarFilter, useDefaultFilters } from "../../../components/calendar/calendarUtils";
import { CalendarLoader } from "../../../components/calendar/CalendarLoader";
import { useIsMobile } from "../../../utils/useIsMobile";
import { useWindowWidth } from "../../../utils/useWindowWith";
import Calendar from "../../../components/calendar/Calendar";
import { calendarFieldsQuery } from "../utils";
import { CalendarFiltersType } from "../../../components/calendar/calendar.types";
import { useSessionsOverlayProps } from "../../../components/calendar/overlay/useSessionsOverlayProps";
import { NeuroAssignTechsWidget } from "./neuro-assign-techs/NeuroAssignTechsWidget";
import { useUser } from "../../../api/useAuth";
import { isNfbAssignWidgetRole } from "../../../utils/constants";

type CalendarProps = {
    setView: React.Dispatch<React.SetStateAction<"calendar" | "list">>;
    clinics: Array<number>;
};

const DEFAULT_VIEW = "day";

export function SessionsCalendar({ setView, clinics }: CalendarProps) {
    const {
        calendar: { calendarFilters, setCalendarFilters, calendarView },
    } = useStore();

    const currentView = calendarView || DEFAULT_VIEW;

    const overlayProps = useSessionsOverlayProps({ calendarFilters, defaultView: DEFAULT_VIEW });

    const defaultFilters = useDefaultFilters({
        status: ["booked", "confirmed", "awaiting_in_clinic", "in_session", "done"],
        clinic_id: clinics,
        view: currentView,
    });

    const mergedFormDefaults = {
        status: calendarFilters?.status ?? defaultFilters.status,
        clinic_id: calendarFilters?.clinic_id ?? defaultFilters.clinic_id,
        technician: calendarFilters?.technician ?? [],
        technicianFilterMode: calendarFilters?.technicianFilterMode ?? "session",
        service_category: calendarFilters?.service_category ?? [],
        dateRange: calendarFilters?.dateRange ?? defaultFilters.dateRange,
        view: calendarFilters?.view ?? defaultFilters.view,
    };

    const entityForm = useForm({
        defaultValues: mergedFormDefaults,
    });

    const watchFields = entityForm.watch;
    const isMobile = useIsMobile();
    const windowWith = useWindowWidth();
    const { data: user } = useUser();

    const { data, status, refetch } = useRecords("sessions", calendarFieldsQuery, {
        extraOptions: {
            filter: createCalendarFilter(calendarFilters),
            pageSize: 1000,
        },
        enabled: calendarFilters ? true : false,
        fetch: {
            fetchInterval: 180000,
        },
    });

    useEffect(() => {
        if (calendarFilters === null) {
            setCalendarFilters(defaultFilters);
        }
    }, [calendarFilters, defaultFilters, setCalendarFilters]);

    useEffect(() => {
        if (!calendarFilters) return;

        const subscription = watchFields((value) => {
            if (calendarFilters !== value) {
                const { dateRange, view,...formFilters } = value;

                setCalendarFilters({
                    ...calendarFilters,
                    ...formFilters,
                } as CalendarFiltersType);
            }
        });
        return () => subscription.unsubscribe();
    }, [watchFields, entityForm, setCalendarFilters, calendarFilters, refetch]);

    if (status === "error") return null;

    const filteredClinics = (calendarFilters?.clinic_id ?? clinics)
        .map((clinicId) => Number(clinicId))
        .filter((clinicId) => Number.isFinite(clinicId));

    return (
        <Box style={{ width: isMobile ? windowWith - 16 : "100%", backgroundColor: "#fff" }}>
            {filteredClinics.length === 1 && isNfbAssignWidgetRole(user?.role?.id) ? (
                <NeuroAssignTechsWidget clinics={filteredClinics} />
            ) : null}
            <SessionsCalendarHeader entityForm={entityForm} calendarFilters={calendarFilters} setView={setView} />
            {status === "loading" ? <CalendarLoader /> : null}
            {
                <Calendar
                    items={data?.items || []}
                    refetch={refetch}
                    isMobile={isMobile}
                    views={{ week: true, month: true, agenda: true, day: true, year: false }}
                    defaultView={isMobile ? "agenda" : DEFAULT_VIEW}
                    {...overlayProps}
                />
            }
        </Box>
    );
}
